import { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import Navbar from '../components/Navbar'
import ImageUpload from '../components/ImageUpload'
import { supabase } from '../lib/supabase'
import { useAuth } from '../lib/AuthContext'
import { flagFields } from '../lib/profanity'

const DISC_COLORS = {
  windsurf:'#0369a1', kite:'#7c3aed', wing:'#059669',
  windfoil:'#b45309', multi:'#be123c'
}

export default function MyListingsPage() {
  const { user, role } = useAuth()
  const navigate = useNavigate()
  const [listings, setListings]   = useState([])
  const [loading, setLoading]     = useState(true)
  const [editing, setEditing]     = useState(null)   // listing being edited
  const [form, setForm]           = useState({})
  const [saving, setSaving]       = useState(false)
  const [msg, setMsg]             = useState('')
  const [error, setError]         = useState('')

  useEffect(() => {
    if (!user) { navigate('/'); return }
    fetchMyListings()
  }, [user])

  async function fetchMyListings() {
    const query = role >= 200
      ? supabase.from('listings').select('*').order('created_at', { ascending: false })
      : supabase.from('listings').select('*').eq('submitted_by', user.id).order('created_at', { ascending: false })

    const { data } = await query
    setListings(data || [])
    setLoading(false)
  }

  function startEdit(listing) {
    setEditing(listing.id)
    setForm({ ...listing })
    setError('')
    window.scrollTo({ top: 0, behavior: 'smooth' })
  }

  function cancelEdit() {
    setEditing(null)
    setForm({})
    setError('')
  }

  async function handleSave(e) {
    e.preventDefault()
    setError('')

    if (!form.name?.trim() || !form.description?.trim() || !form.city?.trim()) {
      setError('Name, City, and Description are required.'); return
    }

    const flagged = flagFields(form)
    setSaving(true)

    const { error: dbErr } = await supabase
      .from('listings')
      .update({
        name:          form.name,
        category:      form.category,
        discipline:    form.discipline,
        region:        form.region,
        city:          form.city,
        country:       form.country,
        type_label:    form.type_label,
        description:   form.description,
        website_url:   form.website_url,
        facebook_url:  form.facebook_url,
        instagram_url: form.instagram_url,
        image_url:     form.image_url,
        status:        flagged ? 'flagged' : form.status === 'approved' ? 'approved' : 'pending',
      })
      .eq('id', editing)

    setSaving(false)

    if (dbErr) { setError(dbErr.message); return }

    setListings(ls => ls.map(l => l.id === editing ? { ...l, ...form } : l))
    setEditing(null)
    setForm({})
    setMsg(flagged ? 'Listing updated but flagged for review.' : 'Listing updated successfully!')
    setTimeout(() => setMsg(''), 4000)
  }

  async function handleDelete(id, name) {
    if (!window.confirm(`Delete "${name}"? This cannot be undone.`)) return
    await supabase.from('listings').delete().eq('id', id)
    setListings(ls => ls.filter(l => l.id !== id))
    setMsg('Listing deleted.')
    setTimeout(() => setMsg(''), 3000)
  }

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }))

  const STATUS_STYLE = {
    approved: { bg:'#d1fae5', color:'#065f46', label:'✅ Live' },
    pending:  { bg:'#fef3c7', color:'#92400e', label:'⏳ Pending Review' },
    flagged:  { bg:'#fee2e2', color:'#dc2626', label:'🚩 Flagged' },
    rejected: { bg:'#f3f4f6', color:'#6b7280', label:'❌ Rejected' },
  }

  if (!user) return null

  return (
    <div>
      <Navbar />
      <div className="page-wrap" style={{ maxWidth: 760 }}>

        <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom:24, flexWrap:'wrap', gap:12 }}>
          <div>
            <div className="page-title">
              {role >= 200 ? 'All Listings' : 'My Listings'}
            </div>
            <div className="page-sub" style={{ marginBottom:0 }}>
              {role >= 200
                ? 'Viewing all listings as admin — you can edit or delete any listing'
                : 'Listings you\'ve submitted — edit or delete anytime'}
            </div>
          </div>
          <button onClick={() => navigate('/submit')} className="btn btn-primary">
            + Submit New Listing
          </button>
        </div>

        {msg && (
          <div style={{ background:'#d1fae5', color:'#065f46', padding:'12px 16px', borderRadius:8, fontSize:13, marginBottom:20 }}>
            {msg}
          </div>
        )}

        {/* Edit form */}
        {editing && (
          <div style={{ background:'#fff', border:'1.5px solid var(--blue)', borderRadius:14, padding:24, marginBottom:28 }}>
            <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom:20 }}>
              <div style={{ fontWeight:700, fontSize:16 }}>Editing: {form.name}</div>
              <button onClick={cancelEdit} className="btn btn-ghost" style={{ fontSize:13 }}>Cancel</button>
            </div>

            {error && (
              <div style={{ background:'#fee2e2', color:'#dc2626', padding:'10px 14px', borderRadius:8, fontSize:13, marginBottom:16 }}>
                {error}
              </div>
            )}

            <form onSubmit={handleSave}>
              <div className='form-grid-2' style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:12 }}>

                <div className="field" style={{ gridColumn:'1/-1' }}>
                  <label>Name *</label>
                  <input value={form.name || ''} onChange={e => set('name', e.target.value)} required />
                </div>

                <div className="field">
                  <label>Category</label>
                  <select value={form.category || 'club'} onChange={e => set('category', e.target.value)}>
                    <option value="club">Club</option>
                    <option value="school">School</option>
                    <option value="shop">Shop</option>
                    <option value="association">Association</option>
                    <option value="online">Online Community</option>
                  </select>
                </div>

                <div className="field">
                  <label>Discipline</label>
                  <select value={form.discipline || 'multi'} onChange={e => set('discipline', e.target.value)}>
                    <option value="windsurf">Windsurfing</option>
                    <option value="kite">Kiteboarding</option>
                    <option value="wing">Wingfoil</option>
                    <option value="windfoil">Windfoil</option>
                    <option value="multi">Multi-discipline</option>
                  </select>
                </div>

                <div className="field">
                  <label>City *</label>
                  <input value={form.city || ''} onChange={e => set('city', e.target.value)} required />
                </div>

                <div className="field">
                  <label>Country</label>
                  <input value={form.country || ''} onChange={e => set('country', e.target.value)} />
                </div>

                <div className="field" style={{ gridColumn:'1/-1' }}>
                  <label>Region</label>
                  <select value={form.region || 'North America'} onChange={e => set('region', e.target.value)}>
                    {['North America','Europe','Africa & Middle East','Caribbean','Pacific & Oceania','Asia','Other'].map(r => (
                      <option key={r} value={r}>{r}</option>
                    ))}
                  </select>
                </div>

                <div className="field" style={{ gridColumn:'1/-1' }}>
                  <label>Type label</label>
                  <input value={form.type_label || ''} onChange={e => set('type_label', e.target.value)} placeholder="e.g. School & Shop · Columbia Gorge" />
                </div>

                <div className="field" style={{ gridColumn:'1/-1' }}>
                  <label>Description *</label>
                  <textarea value={form.description || ''} onChange={e => set('description', e.target.value)} required />
                </div>

                <div className="field" style={{ gridColumn:'1/-1' }}>
                  <label>Website URL</label>
                  <input type="url" value={form.website_url || ''} onChange={e => set('website_url', e.target.value)} placeholder="https://…" />
                </div>

                <div className="field">
                  <label>Facebook URL</label>
                  <input type="url" value={form.facebook_url || ''} onChange={e => set('facebook_url', e.target.value)} placeholder="https://facebook.com/…" />
                </div>

                <div className="field">
                  <label>Instagram URL</label>
                  <input type="url" value={form.instagram_url || ''} onChange={e => set('instagram_url', e.target.value)} placeholder="https://instagram.com/…" />
                </div>

                <div className="field" style={{ gridColumn:'1/-1' }}>
                  <label>Photo</label>
                  <ImageUpload value={form.image_url || ''} onChange={v => set('image_url', v)} />
                </div>

              </div>

              <div style={{ display:'flex', gap:10, marginTop:8 }}>
                <button type="submit" className="btn btn-primary" style={{ flex:1 }} disabled={saving}>
                  {saving ? 'Saving…' : '💾 Save Changes'}
                </button>
                <button type="button" onClick={cancelEdit} className="btn btn-ghost">
                  Cancel
                </button>
              </div>
            </form>
          </div>
        )}

        {/* Listings list */}
        {loading ? (
          <p style={{ textAlign:'center', color:'var(--gray-400)', padding:'60px 0' }}>Loading your listings…</p>
        ) : listings.length === 0 ? (
          <div style={{ textAlign:'center', padding:'60px 20px' }}>
            <div style={{ fontSize:44, marginBottom:14 }}>🌬️</div>
            <h3 style={{ fontSize:20, fontWeight:700, color:'var(--gray-700)', marginBottom:8 }}>No listings yet</h3>
            <p style={{ color:'var(--gray-400)', marginBottom:24 }}>Submit your first club, school, or shop listing.</p>
            <button onClick={() => navigate('/submit')} className="btn btn-primary">Submit a Listing</button>
          </div>
        ) : (
          <div style={{ display:'flex', flexDirection:'column', gap:12 }}>
            {listings.map(l => {
              const status = STATUS_STYLE[l.status] || STATUS_STYLE.pending
              const isEditing = editing === l.id
              return (
                <div key={l.id} style={{
                  background: '#fff',
                  border: `1.5px solid ${isEditing ? 'var(--blue)' : 'var(--gray-200)'}`,
                  borderRadius: 12,
                  padding: '16px 18px',
                  display: 'flex',
                  gap: 14,
                  alignItems: 'flex-start',
                  transition: 'border-color .2s',
                }}>
                  {/* Thumbnail */}
                  <div style={{
                    width: 72, height: 72, borderRadius: 8, overflow:'hidden',
                    background: 'var(--gray-100)', flexShrink: 0,
                  }}>
                    <img
                      src={l.image_url || `https://images.unsplash.com/photo-1559827260-dc66d52bef19?w=200&q=60`}
                      alt={l.name}
                      style={{ width:'100%', height:'100%', objectFit:'cover' }}
                      onError={e => { e.target.style.display = 'none' }}
                    />
                  </div>

                  {/* Info */}
                  <div style={{ flex:1, minWidth:0 }}>
                    <div style={{ display:'flex', alignItems:'flex-start', gap:8, flexWrap:'wrap', marginBottom:4 }}>
                      <div style={{ fontWeight:700, fontSize:15, color:'var(--gray-900)' }}>{l.name}</div>
                      {l.is_featured && <span style={{ fontSize:10, background:'var(--gold)', color:'#fff', padding:'2px 7px', borderRadius:4, fontFamily:'var(--mono)', fontWeight:700 }}>⭐ FEATURED</span>}
                      {l.is_verified && <span style={{ fontSize:10, background:'var(--green)', color:'#fff', padding:'2px 7px', borderRadius:4, fontFamily:'var(--mono)', fontWeight:700 }}>✅ VERIFIED</span>}
                    </div>

                    <div style={{ fontSize:12, color:'var(--gray-500)', marginBottom:6 }}>
                      📍 {l.city}{l.country ? `, ${l.country}` : ''} ·
                      <span style={{ marginLeft:5, fontFamily:'var(--mono)', fontSize:10, textTransform:'uppercase', color: DISC_COLORS[l.discipline] || 'var(--gray-500)' }}>
                        {l.discipline}
                      </span>
                    </div>

                    <div style={{ display:'flex', alignItems:'center', gap:8, flexWrap:'wrap' }}>
                      <span style={{ fontSize:11, fontWeight:600, fontFamily:'var(--mono)', padding:'2px 8px', borderRadius:4, background: status.bg, color: status.color }}>
                        {status.label}
                      </span>
                      <span style={{ fontSize:11, color:'var(--gray-400)', fontFamily:'var(--mono)' }}>
                        ▲ {l.vote_count || 0} votes
                      </span>
                      <span style={{ fontSize:11, color:'var(--gray-400)' }}>
                        Added {new Date(l.created_at).toLocaleDateString('en-US', { month:'short', day:'numeric', year:'numeric' })}
                      </span>
                    </div>
                  </div>

                  {/* Actions */}
                  <div style={{ display:'flex', flexDirection:'column', gap:6, flexShrink:0 }}>
                    <button
                      onClick={() => isEditing ? cancelEdit() : startEdit(l)}
                      className="btn btn-ghost"
                      style={{ fontSize:12, padding:'5px 14px' }}
                    >
                      {isEditing ? 'Cancel' : '✏️ Edit'}
                    </button>
                    <button
                      onClick={() => handleDelete(l.id, l.name)}
                      className="btn btn-danger"
                      style={{ fontSize:12, padding:'5px 14px' }}
                    >
                      🗑 Delete
                    </button>
                  </div>
                </div>
              )
            })}
          </div>
        )}
      </div>
    </div>
  )
}
